import Flex from "@ui-kit/layout/Flex/Flex.web";
import Button from "@ui-kit/form/Button/Button.web";
import Text from "@ui-kit/typography/Text/Text.web";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Icon from "@ui-kit/icons/Icon.web";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import { useTranslation } from "@shared/utils/internationalization";
import { useState } from "react";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { IBasket } from "@shared/modules/basket/interfaces/IBasket";
import { SeatingCartTotalsProps } from "./SeatingCartTotals.props";

const SeatingCartTotals = (props: SeatingCartTotalsProps) => {
  const { t } = useTranslation("seating");

  const {
    venueData,
    overridePurchaseLimits = undefined,
    isEmptyCart,
    minPurchaseInProgress,
    minPurchaseLimit,
    minPurchaseSeatArray,
  } = props;

  const {
    internalFeesTotal,
    discountAmount,
    hasPaymentPlan,
    currency,
    paymentPlanTotalFees,
    serviceFees,
    totalPrice,
    subtotal,
    customFeesGroupedByName,
  } = useBasketService<IBasket>();

  // controls the cart totals display
  const [hideTotals, setHideTotals] = useState(false);

  // fees and taxes to apply to the basket
  const { $customFees, $fees, $taxes } = customFeesGroupedByName ?? {};
  const { getItemTypeInPendingCart } = useAssignedSeatingHelpers();

  return (
    <>
      {/* Show more / Show less Button */}
      <Flex justifyContent="center" data-testid="seating-cart-totals">
        <Button
          data-testid="show-hide-seating-cart-totals"
          variant="ghost"
          hidden={isEmptyCart}
          width="100%"
          onClick={() => {
            setHideTotals(!hideTotals);
          }}
          mb="2xs"
          _hover={{
            backgroundColor: "transparent",
          }}
          _active={{
            backgroundColor: "transparent",
          }}
        >
          <Flex alignItems="center">
            <Text textTransform="uppercase" fontSize="xs">
              {hideTotals
                ? t("Show more", { ns: "common" })
                : t("Show less", { ns: "common" })}
            </Text>
            <Text ml="2xs" mb="2xs">
              {hideTotals ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
            </Text>
          </Flex>
        </Button>
        <Text hidden={!isEmptyCart} fontWeight="bold">
          {t("Select a seat")}
        </Text>
      </Flex>
      {/* Subtotal display */}
      <Flex fontSize="xs" hidden={hideTotals} justifyContent="space-between">
        <Text>{t("Subtotal", { ns: "constants" })}:</Text>
        <Flex>
          <Text hidden={!hasPaymentPlan} color="red.900">
            *
          </Text>
          <CurrencyFormat
            value={subtotal ?? "0.00"}
            displayType="text"
            currency={currency}
          />
        </Flex>
      </Flex>
      {/* Basket discount display */}
      <Flex
        hidden={
          hideTotals ||
          !discountAmount ||
          Boolean(discountAmount && Number(discountAmount) === 0)
        }
        fontSize="xs"
        justifyContent="space-between"
      >
        <Text>{t("Discounts", { ns: "constants" })}:</Text>
        <Text>
          <CurrencyFormat
            value={discountAmount}
            displayType="text"
            currency={currency}
          />
        </Text>
      </Flex>
      {/* Custom fee display */}
      {$customFees?.map((fee) => (
        <Flex
          key={fee.name}
          fontSize="xs"
          justifyContent="space-between"
          hidden={hideTotals || venueData.tax_on_fees || fee.fee_total === 0}
        >
          <Text>{t(fee.name)}:</Text>
          <Text>
            <CurrencyFormat
              value={fee.fee_total}
              displayType="text"
              currency={currency}
            />
          </Text>
        </Flex>
      ))}
      {/* Fee display */}
      {$fees?.map((fee) => (
        <Flex
          key={fee.name}
          fontSize="xs"
          justifyContent="space-between"
          hidden={hideTotals || !venueData.tax_on_fees || fee.fee_total === 0}
        >
          <Text>
            {t(fee.name)}
            {!!fee.tax_number && ` (${fee.tax_number})`}:
          </Text>
          <Text>
            <CurrencyFormat
              value={fee.fee_total}
              displayType="text"
              currency={currency}
            />
          </Text>
        </Flex>
      ))}
      {/* Service fee display */}
      <Flex
        fontSize="xs"
        justifyContent="space-between"
        hidden={
          hideTotals ||
          Boolean(!serviceFees) ||
          Boolean(serviceFees && Number(serviceFees) === 0)
        }
      >
        <Text>{t("Service Fees", { ns: "constants" })}:</Text>
        <Text>
          <CurrencyFormat
            value={internalFeesTotal}
            displayType="text"
            currency={currency}
          />
        </Text>
      </Flex>
      {/* Payment plan fee display */}
      <Flex
        fontSize="xs"
        justifyContent="space-between"
        hidden={
          hideTotals ||
          Boolean(!paymentPlanTotalFees) ||
          Boolean(paymentPlanTotalFees && Number(paymentPlanTotalFees) === 0)
        }
      >
        <Text>{t("Payment Plan Fees", { ns: "constants" })}:</Text>
        <Text>
          <CurrencyFormat
            value={paymentPlanTotalFees}
            displayType="text"
            currency={currency}
          />
        </Text>
      </Flex>
      {/* Tax display */}
      {$taxes?.map((tax) => (
        <Flex
          key={tax.name}
          fontSize="xs"
          justifyContent="space-between"
          hidden={hideTotals || !venueData.tax_on_fees}
        >
          <Text>
            {t(tax.name)}
            {!!tax.tax_number && ` (${tax.tax_number})`}:
          </Text>
          <Text>
            <CurrencyFormat
              value={tax.fee_total}
              displayType="text"
              currency={currency}
            />
          </Text>
        </Flex>
      ))}
      {/* Payment plan display */}
      <Text hidden={!hasPaymentPlan} fontSize="xs" color="red.900">{`* ${t(
        "Total of first payment of payment plan selected"
      )}`}</Text>
      {/* Displays minimum purchase limit for overridden limits or item-level limits */}
      {minPurchaseInProgress && (
        <Flex
          color="secondary.600"
          fontSize="xs"
          justifyContent="space-between"
        >
          <Flex alignItems="center">
            <Icon.DangerCircle boxSize="element.md" mr="2xs" />
            <Box>
              {/* Hide item name when the purchase limit includes multiple item types */}
              {!overridePurchaseLimits?.multiItemsAllowed && (
                <Text maxWidth="120px" isTruncated>
                  {getItemTypeInPendingCart()?.name}
                </Text>
              )}
            </Box>
          </Flex>
          <Text>
            {overridePurchaseLimits?.multiItemsAllowed
              ? t("Minimum {{count}} items required", {
                  count: minPurchaseLimit,
                })
              : `${minPurchaseSeatArray.length} ${t("of")} ${minPurchaseLimit}`}
          </Text>
        </Flex>
      )}
      <Flex fontWeight="bold" justifyContent="space-between">
        <Text>{t("Total", { ns: "constants" })}:</Text>
        <Text>
          <CurrencyFormat
            value={totalPrice ?? "0.00"}
            displayType="text"
            currency={currency}
          />
        </Text>
      </Flex>
    </>
  );
};

export default SeatingCartTotals;
